import DataGrid, { Column, Editing, Format } from 'devextreme-react/data-grid';
import { useCallback, useContext } from 'react';
import { AR, OrderStatus, ProductLine } from '../../../../types/enums';
import { PurchaseOrderContext } from '../context/PurchaseOrderContext';
import { POProduct } from '../types/POProduct';

export default function PurchaseOrderDeliveryProducts() {

    const { order, modifyOrder } = useContext(PurchaseOrderContext);

    const isLaminate = order.details.products.some((p) => {
        return p.productLineID === ProductLine.LaminateInstall || p.productLineID === ProductLine.LaminateDelivered;
    });

    const isOrderReadonly = order.details.orderStatusID === OrderStatus.Closed ||
        order.details.orderStatusID === OrderStatus.Cancelled ||
        order.details.resolvedARs.includes(AR.PODistributorToInvoice);

    const products = order.details.products?.map(x => {
        return { ...x, qtyDelivered: x.qtyDelivered === 0 && !x.isGraniteSlab ? x.qtyOrdered : x.qtyDelivered }
    }) ?? [];

    const displayDescription = useCallback((rowData: POProduct) =>
        rowData.itemDescription.replace(new RegExp('\\r\\n', 'g'), '<br/>'), []);

    const displayTotalCost = useCallback((rowData: POProduct) => {
        return (rowData.qtyDelivered ?? 0) * rowData.unitCost + (rowData.freightCost ?? 0);
    }, []);

    return (
        <>
            <div className='dx-card wide-card'>
                <div className='grid-header'>Purchase Order Details</div>
                <DataGrid
                    dataSource={products}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    showBorders={true}
                    showColumnLines={true}
                    columnAutoWidth={true}
                    wordWrapEnabled={true}
                    keyExpr={'poProductID'}
                    className="grid-with-header"
                    onRowUpdated={(e) => {
                        const rowIndex = e.component.getRowIndexByKey(e.key);
                        if (products[rowIndex].backOrdered) {
                            products[rowIndex].deliveredDate = undefined;
                        }
                        else {
                            products[rowIndex].estimatedDeliveryDate = undefined;
                        }
                        modifyOrder({ ...order.details, products: products });
                    }}
                    onEditorPreparing={(e) => {
                        if (e.dataField === "deliveredDate") {
                            e.editorOptions.readOnly = e.row?.data.backOrdered;
                        }

                        if (e.dataField === "estimatedDeliveryDate") {
                            e.editorOptions.readOnly = !e.row?.data.backOrdered;
                        }
                    }}
                >
                    <Editing
                        mode="cell"
                        allowDeleting={false}
                        allowUpdating={order.details.orderStatusID === OrderStatus.Open}
                    />
                    <Column
                        dataField={'sku'}
                        caption={'SKU'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'itemDescription'}
                        caption={'Item Description'}
                        dataType="string"
                        encodeHtml={false}
                        calculateDisplayValue={displayDescription}
                        allowEditing={false}
                        width={400}
                    />
                    <Column
                        dataField={'thickness'}
                        caption={'Thickness'}
                        dataType="string"
                        visible={!isLaminate}
                        allowEditing={false}
                    />
                    <Column
                        dataField={'dimensions'}
                        caption={'Dimensions'}
                        dataType="string"
                        visible={!isLaminate}
                        allowEditing={false}
                    />
                    <Column
                        dataField={'squareFootage'}
                        caption={'Square Footage'}
                        dataType="string"
                        visible={!isLaminate}
                        allowEditing={false}
                    />
                    <Column
                        dataField={'qtyOrdered'}
                        caption={'Qty Ordered'}
                        dataType="number"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'qtyDelivered'}
                        caption={'Qty Delivered'}
                        dataType="number"
                        allowEditing={!isOrderReadonly}
                        editorOptions={{
                            showSpinButtons: true,
                            min: 0,
                            format: "##0.###"
                        }}
                        showEditorAlways={!isOrderReadonly}
                        width={80}
                        cssClass={!isOrderReadonly ? 'required' : ''}
                    />
                    <Column
                        dataField={'unitCost'}
                        caption={'Unit Cost'}
                        dataType="number"
                        allowEditing={false}
                    >
                        <Format type="currency" precision={2} ></Format>
                    </Column>
                    <Column
                        dataField={'freightCost'}
                        caption={'Freight Cost'}
                        dataType="number"
                        allowEditing={false}
                    >
                        <Format type="currency" precision={2} ></Format>
                    </Column>
                    <Column
                        caption={'Total Cost'}
                        dataType="number"
                        allowEditing={false}
                        calculateDisplayValue={displayTotalCost}
                    >
                        <Format type="currency" precision={2} ></Format>
                    </Column>
                    <Column
                        dataField={'carrier'}
                        caption={'Carrier'}
                        dataType="string"
                        showEditorAlways={!isOrderReadonly}
                        allowEditing={!isOrderReadonly}
                    />
                    <Column
                        dataField={'trackingNbr'}
                        caption={'Tracking Number'}
                        dataType="string"
                        allowEditing={!isOrderReadonly}
                        showEditorAlways={!isOrderReadonly}
                    />
                    <Column
                        dataField={'deliveredDate'}
                        caption={'Actual Delivery Date'}
                        dataType="date"
                        allowEditing={!isOrderReadonly}
                        showEditorAlways={!isOrderReadonly}
                        editorOptions={{
                            showClearButton: false,
                        }}
                        width={100}
                        cssClass={!isOrderReadonly ? 'required' : ''}
                    />
                    <Column
                        dataField={'backOrdered'}
                        caption={'Back Ordered'}
                        dataType="boolean"
                        allowEditing={!isOrderReadonly}
                        showEditorAlways={true}
                        visible={!order.details.isSampleOrder}
                    />
                    <Column
                        dataField={'estimatedDeliveryDate'}
                        caption={'Estimated Delivery Date'}
                        dataType="date"
                        allowEditing={!isOrderReadonly}
                        showEditorAlways={!isOrderReadonly}
                        editorOptions={{
                            showClearButton: false,
                        }}
                        width={100}
                        visible={!order.details.isSampleOrder}
                    />
                </DataGrid>
            </div>
        </>
    );
}
